import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import styles from './DefinitionList.module.css';

export interface DefinitionListItem {
  /** Stable key. Use the ontology property name where there is one. */
  id?: string;
  term: ReactNode;
  description: ReactNode;
  /** Mono value — canonical ids, counts, durations. */
  monospace?: boolean;
}

export interface DefinitionListProps extends HTMLAttributes<HTMLDListElement> {
  items: readonly DefinitionListItem[];
  /**
   * `rows` puts term and value side by side (the inspector identity block);
   * `stacked` puts the value under the term (narrow columns).
   */
  layout?: 'rows' | 'stacked';
  dense?: boolean;
}

/** A real `dl`: the term/value relationship is structure, not a two-column grid. */
export const DefinitionList = forwardRef<HTMLDListElement, DefinitionListProps>(
  function DefinitionList({ items, layout = 'rows', dense = false, className, ...rest }, ref) {
    return (
      <dl
        {...rest}
        ref={ref}
        className={clsx(styles.list, styles[layout], dense && styles.dense, className)}
      >
        {items.map((item, index) => (
          <div className={styles.row} key={item.id ?? index}>
            <dt className={styles.term}>{item.term}</dt>
            <dd className={clsx(styles.description, item.monospace === true && styles.mono)}>
              {item.description}
            </dd>
          </div>
        ))}
      </dl>
    );
  },
);
